import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import './Dropdown.scss';
import { ESSButton } from 'volto-essvoltocomponents/components/Atoms';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { SizeProp } from '@fortawesome/fontawesome-svg-core';
import { faSortDown } from '@fortawesome/free-solid-svg-icons';
import { clsx } from 'clsx';
/**
 * Represents a single option in the dropdown menu.  An option can either
 * navigate to a URL (via `href`) or execute a callback (via `onSelect`).
 * Exactly one of `href` or `onSelect` should be provided.  If both are
 * undefined, the item will be rendered as disabled.
 */
export interface DropdownItem {
  /**
   * Unique identifier for this menu item.  Useful as a React `key`.
   */
  id: string;
  /**
   * Text to display for this menu item.
   */
  label: string;
  /**
   * URL to navigate to when the item is selected.  If provided, the item
   * will be rendered as an `<a>` element with this `href`.
   */
  href?: string;
  /**
   * Callback invoked when the item is selected.  Use this for actions
   * handled client–side.  If defined, the item will be rendered as a
   * `<button>` that triggers this callback.
   */
  onSelect?: () => void;
  /**
   * Whether the item is disabled.  Disabled items are not focusable and
   * cannot be activated.
   */
  disabled?: boolean;
}

export interface DropdownProps {
  /**
   * Text for the button that toggles the dropdown.  Typically describes
   * the set of actions contained in the menu.
   */
  label: string;
  /**
   * Array of items to display in the dropdown menu.  Each item should
   * either specify an `href` or an `onSelect` callback.
   */
  items: DropdownItem[];
  /**
   * Optional custom class name to apply to the root element for additional
   * styling hooks.
   */
  className?: string;
  /** 
   * Size of the trigger button
   */
  triggerSize?: 'sm' | 'md' | 'lg';
    /** 
   * Size of the option list item button 
   */
  menuItemSize?: 'sm' | 'md' | 'lg';
}

/**
 * A basic dropdown (pop‑up menu) component.  Renders a button that
 * toggles visibility of a menu containing actions or links.  The menu
 * closes automatically when clicking outside or selecting an item.  Focus
 * management is basic; for advanced accessibility requirements, consider
 * using a dedicated menu component from React Aria.
 */
const Dropdown: React.FC<DropdownProps> = ({ 
  label, 
  items, 
  className,
  triggerSize = 'md',
  menuItemSize = triggerSize,
 }) => {
  const [open, setOpen] = useState(false);
  const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLUListElement | null>(null);

  const triggerButtonClasses = clsx(
    'dropdown__button',
    `dropdown__button--${triggerSize}`,
    className
  );

  const menuClasses = clsx(
    'dropdown__menu',
    `dropdown__menu--${menuItemSize}`,
    className
  );

  const menuItemClasses = clsx(
    'dropdown__item',
    `dropdown__item--${menuItemSize}`,
    className
  );

  const IconSize = triggerSize as SizeProp;
  
  useEffect(() => {
    if (typeof document === 'undefined') return;
    let root = document.getElementById('ess-dropdown-portal-root');
    if (!root) {
      root = document.createElement('div');
      root.id = 'ess-dropdown-portal-root';
      document.body.appendChild(root);
    }
    setPortalRoot(root);
  }, []);

  const updateMenuPosition = () => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();

    setMenuStyle({
      position: 'absolute',
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
      width: rect.width,
      minWidth: rect.width,
    });
  };

  useLayoutEffect(() => {
    if (!open) return;

    updateMenuPosition();

    window.addEventListener('resize', updateMenuPosition);

    return () => {
      window.removeEventListener('resize', updateMenuPosition);
    };
  }, [open]);

  // Close the menu when clicking outside the dropdown
  useEffect(() => {
    function handleDocumentClick(event: MouseEvent) {
      const target = event.target as Node | null;

      if (!open) return;

      if (
        target &&
        buttonRef.current &&
        menuRef.current &&
        !buttonRef.current.contains(target) &&
        !menuRef.current.contains(target)
      ) {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handleDocumentClick);

    return () =>
      document.removeEventListener('mousedown', handleDocumentClick);
  }, [open]);

  // Close the menu when an item is selected
  function handleItemSelect(item: DropdownItem) {
    if (item.disabled) return;

    setOpen(false);

    if (item.onSelect) {
      item.onSelect();
    }
  }

  const menu = (
    <ul
      ref={menuRef}
      className={menuClasses}
      role="menu"
      style={menuStyle}
      tabIndex={-1}
    >
      {items.map((item) => {
        const isDisabled = item.disabled || (!item.href && !item.onSelect);

        const key = item.id;

        // Render an anchor when href is provided; otherwise a button
        const content = item.href ? (
          <a
            className="dropdown__item"
            href={isDisabled ? undefined : item.href}
            role="menuitem"
            aria-disabled={isDisabled}
            onClick={() => setOpen(false)}
          >
            {item.label}
          </a>
        ) : (
          <ESSButton
            variant="negative"
            className={menuItemClasses}
            role="menuitem"
            disabled={isDisabled}
            aria-disabled={isDisabled}
            onClick={() => handleItemSelect(item)}
          >
            {item.label}
          </ESSButton>
        );

        return (
          <li
            key={key}
            className={[
              'dropdown__list-item',
              isDisabled ? 'dropdown__list-item--disabled' : undefined,
            ]
              .filter(Boolean)
              .join(' ')}
            role="none"
          >
            {content}
          </li>
        );
      })}
    </ul>
  );

  return (
    <div className={['dropdown', className].filter(Boolean).join(' ')}>
      <ESSButton
        variant="negative"
        ref={buttonRef}
        className={triggerButtonClasses}
        aria-haspopup="true"
        aria-expanded={open}
        fullWidth={true}
        onClick={() => setOpen((prev) => !prev)}
      >
        {label}

        <span className="dropdown__icon" aria-hidden="true">
          <FontAwesomeIcon icon={faSortDown} size={IconSize} />
        </span>
      </ESSButton>

      {open && portalRoot && createPortal(menu, portalRoot)}
    </div>
  );
};

export default Dropdown;